'use client';

import Image from 'next/image'
import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import Asterisk from '../assets/Asterisk';
import Infinity from '../assets/Infinity';
import Jigger from '../assets/Jigger';
import LeftRight from '../assets/LeftRight';
import ArrowHandDrawn from '../assets/ArrowHandDrawn';
import AsteriskHandDrawn from '../assets/AsteriskHandDrawn';

type HeroHomepageProps = {
  id: string;
};

export default function HeroHomepage({ id }: HeroHomepageProps) {
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasFadedOut, setHasFadedOut] = useState(false);
  const hasFadedOutRef = useRef(false);
  const [isInView, setIsInView] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);
  const isAnimatingRef = useRef(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const heroTextRef = useRef<HTMLHeadingElement>(null);
  const heroTextMobileRef = useRef<HTMLHeadingElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const iconsDesktopRef = useRef<HTMLHeadingElement>(null);
  const iconsMobileRef = useRef<HTMLHeadingElement>(null);
  const asteriskDesktopRef = useRef<SVGSVGElement>(null);
  const asteriskMobileRef = useRef<SVGSVGElement>(null);

  // Check for reduced motion preference
  useEffect(() => {
    const checkReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    checkReducedMotion(); // Initial check
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    // Listen for changes (some browsers support this)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Track screen size changes
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile(); // Initial check
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Intersection Observer to track if hero is in view
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (hasFadedOut) return; // Once faded out, stop listening
      
      const scrollY = window.scrollY;
      // Fade out over first 200px of scroll
      const opacity = Math.max(0, 1 - scrollY / 200);
      setScrollOpacity(opacity);
      
      // Lock it at 0 once fully faded
      if (opacity === 0) {
        setHasFadedOut(true);
        hasFadedOutRef.current = true;
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasFadedOut]);

  // Update ref when state changes
  useEffect(() => {
    isAnimatingRef.current = isAnimating;
  }, [isAnimating]);

  // Continuous random animation between Ivy italic and Ivy normal
  useEffect(() => {
    // Don't animate if user prefers reduced motion
    if (prefersReducedMotion) {
      return;
    }
    
    // Only get spans for the current screen size
    const allSpans = isMobile && heroTextMobileRef.current
      ? Array.from(heroTextMobileRef.current.querySelectorAll('span.hero-char'))
      : !isMobile && heroTextRef.current
      ? Array.from(heroTextRef.current.querySelectorAll('span.hero-char'))
      : [];
    
    // Filter out spans with no-animate class
    const spans = allSpans.filter(span => !span.classList.contains('no-animate'));
    
    if (spans.length === 0) return;
    
    // Store initial italic state (characters that have 'italic' class)
    const initialItalicSpans = spans.filter(span => span.classList.contains('italic'));
    
    // Set initial state - characters with 'italic' class should be italic, others normal
    spans.forEach(span => {
      if (span.classList.contains('italic')) {
        gsap.set(span, { fontStyle: 'italic' });
      } else {
        gsap.set(span, { fontStyle: 'normal' });
      }
    });

    const animateRandomChars = () => {
      // Separate spans into italic and normal
      const italicSpans = spans.filter(span => 
        window.getComputedStyle(span).fontStyle === 'italic'
      );
      const _normalSpans = spans.filter(span => 
        window.getComputedStyle(span).fontStyle === 'normal'
      );

      // Pick 1 random character to animate
      const selected = [];

      // If we're at or over the limit, pick from italic chars to turn normal
      if (italicSpans.length >= 4) {
        const shuffled = [...italicSpans].sort(() => 0.5 - Math.random());
        selected.push(shuffled[0]);
      } 
      // If we have room, pick randomly from all chars
      else {
        const shuffled = [...spans].sort(() => 0.5 - Math.random());
        selected.push(shuffled[0]);
      }

      selected.forEach((span) => {
        const currentStyle = window.getComputedStyle(span).fontStyle;
        const newStyle = currentStyle === 'italic' ? 'normal' : 'italic';
        
        // Double-check we don't exceed limit
        if (newStyle === 'italic' && italicSpans.length >= 4) {
          return;
        }
        
        gsap.to(span, {
          fontStyle: newStyle,
          duration: 0.4,
          ease: 'power2.inOut'
        });
      });
    };

    let timeoutId: NodeJS.Timeout | null = null;
    
    const scheduleNext = () => {
      const randomDelay = Math.random() * 25 + 150;
      timeoutId = setTimeout(() => {
        // Check ref for current animation state (not closure value)
        if (isInView && isAnimatingRef.current) {
          animateRandomChars();
          scheduleNext();
        }
      }, randomDelay);
    };
    
    // Start scheduling if animation is active
    if (isAnimating) {
      scheduleNext();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInView, isMobile, isAnimating, prefersReducedMotion]);

  // Stop animation after 5 seconds and restore initial italic state
  useEffect(() => {
    // Don't run timer if user prefers reduced motion
    if (prefersReducedMotion) {
      return;
    }
    
    const stopTimeoutId = setTimeout(() => {
      setIsAnimating(false);
      
      // Restore initial italic state when animation stops
      const allSpans = isMobile && heroTextMobileRef.current
        ? Array.from(heroTextMobileRef.current.querySelectorAll('span.hero-char'))
        : !isMobile && heroTextRef.current
        ? Array.from(heroTextRef.current.querySelectorAll('span.hero-char'))
        : [];
      
      const spans = allSpans.filter(span => !span.classList.contains('no-animate'));
      
      spans.forEach(span => {
        if (span.classList.contains('italic')) {
          gsap.to(span, {
            fontStyle: 'italic',
            duration: 0.4,
            ease: 'power2.inOut'
          });
        } else {
          gsap.to(span, {
            fontStyle: 'normal',
            duration: 0.4,
            ease: 'power2.inOut'
          });
        }
      });
    }, 5000);

    return () => {
      clearTimeout(stopTimeoutId);
    };
  }, [isMobile, prefersReducedMotion]); // Include isMobile to handle screen size changes

  // Icon display properties setup (no rotation animation)
  useEffect(() => {
    // Only get icons for the current screen size
    const iconContainer = isMobile ? iconsMobileRef.current : iconsDesktopRef.current;
    if (!iconContainer) return;

    const icons = Array.from(iconContainer.querySelectorAll('.hero-icon'));
    if (icons.length === 0) return;

    // Set display properties to help with centering and clear any rotation
    icons.forEach(icon => {
      const element = icon as HTMLElement;
      element.style.display = 'inline-flex';
      element.style.alignItems = 'center';
      element.style.justifyContent = 'center';
    });
    
    // Explicitly clear any GSAP rotation transforms
    gsap.set(icons, { rotation: 0, clearProps: 'transform' });
  }, [isMobile]);

  // Rotate asterisk left (snap, no animation)
  useEffect(() => {
    // Don't animate if user prefers reduced motion
    if (prefersReducedMotion) {
      return;
    }
    
    const asterisk = isMobile ? asteriskMobileRef.current : asteriskDesktopRef.current;
    if (!asterisk) return;

    // Set initial rotation and transform origin
    gsap.set(asterisk, { rotation: 0, transformOrigin: '50% 50%' });

    const rotateAsterisk = () => {
      const currentRotation = gsap.getProperty(asterisk, 'rotation') as number;
      // Rotate left by 45 degrees (counter-clockwise)
      const newRotation = currentRotation - 45;

      // Snap instantly (no animation)
      gsap.set(asterisk, { rotation: newRotation });
    };

    let timeoutId: NodeJS.Timeout | null = null;

    const scheduleNext = () => {
      const randomDelay = Math.random() * 300 + 1000; // 1000-1300ms
      timeoutId = setTimeout(() => {
        // Check ref for current animation state (not closure value)
        if (isInView && isAnimatingRef.current) {
          rotateAsterisk();
          scheduleNext();
        }
      }, randomDelay);
    };

    // Start scheduling if animation is active
    if (isAnimating) {
      scheduleNext();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInView, isMobile, isAnimating, prefersReducedMotion]);

  return (
    <section ref={sectionRef} className="homepage-hero relative flex flex-col items-center pt-16 lg:pt-24" style={{ minHeight: '100vh' }} id={id}>
      
      {/* Desktop version */}
      <div className="animated-text-desktop hidden mt-10 mb-20 lg:flex w-full max-w-120rem flex-1 items-center justify-center">
        <h1 
          ref={(el) => { heroTextRef.current = el; iconsDesktopRef.current = el; }} 
          className="heading-1-lg max-w-[85%] mx-auto hero-text-container" 
          aria-label="Human experiences delivered intelligently."
          onMouseEnter={() => setIsAnimating(true)}
          onMouseLeave={() => setIsAnimating(false)}
        >
          <span className="hero-char">H</span>
          <span className="hero-char">u</span>
          <span className="hero-char">m</span>
          <span className="hero-char italic">a</span>
          <span className="hero-char">n</span>
          
          <span style={{ display: 'inline-block', verticalAlign: 'middle', transform: 'translateY(-15%)' }}><span className="ml-4 hero-icon inline-flex items-center justify-center"><ArrowHandDrawn /></span></span>
          
          <span className="inline-block w-3"></span>
          <span className="hero-char">e</span>
          <span className="hero-char">x</span>
          <span className="hero-char">p</span>
          <span className="hero-char">e</span>
          <span className="hero-char">r</span>
          <span className="hero-char italic">i</span>
          <span className="hero-char">e</span>
          <span className="hero-char">n</span>
          <span className="hero-char">c</span>
          <span className="hero-char">e</span>
          <span className="hero-char">s</span>
          <span className="hero-char">,</span>

          <span className="hero-line-break"></span>
          
          <span className="hero-char">d</span>
          <span className="hero-char">e</span>
          <span className="hero-char no-animate">l</span>
          <span className="hero-char">i</span>
          <span className="hero-char">v</span>
          <span className="hero-char">e</span>
          <span className="hero-char">r</span>
          <span className="hero-char italic">e</span>
          <span className="hero-char">d</span>

          <span className="inline-block w-6"></span>
          
          <span className="hero-char">i</span>
          <span className="hero-char">n</span>
          <span className="hero-char">t</span>
          <span className="hero-char">e</span>
          <span className="hero-char no-animate">l</span>
          <span className="hero-char no-animate">l</span>
          <span className="hero-char">i</span>
          <span className="hero-char">g</span>
          <span className="hero-char">e</span>
          <span className="hero-char">n</span>
          <span className="hero-char">t</span>
          <span className="hero-char">l</span>
          <span className="hero-char italic">y</span>
          <span className="hero-char">.</span>

          <span style={{ display: 'inline-block', position: 'relative', top: '0.9375rem' }}><span className="hero-icon inline-flex items-center justify-center"><AsteriskHandDrawn svgRef={asteriskDesktopRef} /></span></span>
        </h1>
      </div>

      <div 
        className={`scroll-for-more hidden lg:flex flex-col items-center gap-2 transition-opacity duration-300 cursor-pointer mt-10 mb-4 ${scrollOpacity === 0 ? 'pointer-events-none' : ''}`}
        style={{ opacity: scrollOpacity }}
        onClick={() => {
          window.scrollTo({
            top: window.innerHeight,
            behavior: 'smooth'
          });
        }}
      >
        <p className="text-center !text-[0.75rem] uppercase">SCROLL FOR MORE</p>
        <Image src="/images/underline.svg" alt="" width={160} height={12} className="mx-auto" priority />
      </div>

      {/* Mobile version */}
      <div className="animated-text-mobile lg:hidden w-full max-w-120rem global-padding mx-auto flex-1 flex items-center justify-center pb-[7.5rem] md:pb-[6.25rem]">
        <h1 
          ref={(el) => { heroTextMobileRef.current = el; iconsMobileRef.current = el; }} 
          className="heading-1-lg hero-text-container" 
          aria-label="Human experiences delivered intelligently."
          onMouseEnter={() => setIsAnimating(true)}
          onMouseLeave={() => setIsAnimating(false)}
        >
          <span className="hero-char">H</span>
          <span className="hero-char">u</span>
          <span className="hero-char">m</span>
          <span className="hero-char italic">a</span>
          <span className="hero-char">n</span>

          <span style={{ display: 'inline-block', verticalAlign: 'middle', transform: 'translateY(-15%)' }}><span className="ml-4 hero-icon inline-flex items-center justify-center"><ArrowHandDrawn className="scale-150" /></span></span>

          <span className="hero-line-break"></span>
          
          <span className="hero-char">e</span>
          <span className="hero-char">x</span>
          <span className="hero-char">p</span>
          <span className="hero-char">e</span>
          <span className="hero-char">r</span>
          <span className="hero-char italic">i</span>
          <span className="hero-char">e</span>
          <span className="hero-char">n</span>
          <span className="hero-char">c</span>
          <span className="hero-char">e</span>
          <span className="hero-char">s</span>
          <span className="hero-char">,</span>

          <span className="hero-line-break"></span>
          
          <span className="hero-char">d</span>
          <span className="hero-char">e</span>
          <span className="hero-char no-animate">l</span>
          <span className="hero-char">i</span>
          <span className="hero-char">v</span>
          <span className="hero-char italic">e</span>
          <span className="hero-char">r</span>
          <span className="hero-char">e</span>
          <span className="hero-char">d</span>

          <span style={{ display: 'inline-block', verticalAlign: 'middle' }}><span className="hero-icon inline-flex items-center justify-center"><AsteriskHandDrawn className="scale-150" svgRef={asteriskMobileRef} /></span></span>
          
          <span className="hero-line-break"></span>
          
          <span className="hero-char">i</span>
          <span className="hero-char">n</span>
          <span className="hero-char">t</span>
          <span className="hero-char">e</span>
          <span className="hero-char no-animate">l</span>
          <span className="hero-char no-animate">l</span>
          <span className="hero-char">i</span>
          <span className="hero-char">g</span>
          <span className="hero-char">e</span>
          <span className="hero-char">n</span>
          <span className="hero-char">t</span>
          <span className="hero-char">l</span>
          <span className="hero-char italic">y</span>
          <span className="hero-char">.</span>

        </h1>
        
      </div>
    </section>
  );
}
